import { afterRenderEffect, Component, ElementRef, ViewChild, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';
import { ChatWidgetService } from '../../core/services/chat-widget.service';
import { parseMessageLines } from '../../core/services/chat-message-lines';
import { RbacService } from '../../core/services/rbac.service';

const TIP_DISMISSED_KEY = 'chatWidget.tipDismissed';

/** Always mounted in the shell; visibility is CSS-driven so the conversation survives close/reopen. */
@Component({
  selector: 'app-chat-widget',
  standalone: true,
  imports: [FormsModule, RouterLink],
  templateUrl: './chat-widget.component.html',
  styleUrl: './chat-widget.component.scss',
})
export class ChatWidgetComponent {
  private readonly rbac = inject(RbacService);
  private readonly router = inject(Router);
  readonly widget = inject(ChatWidgetService);

  @ViewChild('composerInput') private composerInput?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('chatBody') private chatBody?: ElementRef<HTMLElement>;

  readonly canUse = computed(() => this.rbac.canModule('assistant'));
  readonly parseMessageLines = parseMessageLines;
  private readonly tipDismissed = signal(this.readTipDismissed());

  constructor() {
    effect(() => {
      const tick = this.widget.focusComposerTick();
      if (!tick || !this.widget.isOpen()) return;
      queueMicrotask(() => {
        const el = this.composerInput?.nativeElement;
        if (!el) return;
        el.focus();
        const end = el.value.length;
        el.setSelectionRange(end, end);
      });
    });

    afterRenderEffect(() => {
      this.widget.messages().length;
      this.widget.asking();
      if (!this.widget.isOpen()) return;
      const el = this.chatBody?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }

  /** Tip bubble only while the FAB is visible (chat closed) and not manually dismissed. */
  readonly showTip = computed(() => !this.widget.isOpen() && !this.tipDismissed());

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url.split('?')[0]),
      startWith(this.router.url.split('?')[0]),
    ),
    { initialValue: this.router.url.split('?')[0] },
  );

  /** The full-page assistant already covers this — no point floating a duplicate over it. */
  readonly showWidget = computed(() => !this.url().startsWith('/assistant'));

  dismissTip(): void {
    this.tipDismissed.set(true);
    try {
      localStorage.setItem(TIP_DISMISSED_KEY, '1');
    } catch {
      /* ignore */
    }
  }

  /** Enter sends; Shift+Enter inserts a newline in the structured draft. */
  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    void this.widget.ask();
  }

  private readTipDismissed(): boolean {
    try {
      return localStorage.getItem(TIP_DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  }
}
